import React from 'react';
import { CommissionLine } from '../../types/commission';
import { isPlanRuleKey } from '../../utils/commission';
import { formatBps, sumCents } from '../../utils/format';
import { Badge } from '../sf/Badge';
import { Td, Th } from '../sf/DataTable';
import { Money } from '../sf/Money';
import { NewBadge } from '../sf/NewBadge';
import { StatusBadge } from '../sf/StatusBadge';
import { StoredReference } from '../sf/StoredReference';
interface CommissionLinesTableProps {
  lines: CommissionLine[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onRuleKeyClick: () => void;
  showTotal?: boolean;
  showLoanNumber?: boolean;
}
const isOverrideRow = (line: CommissionLine) => line.component === 'TL Rate' || line.component === 'Director/DL';
export function CommissionLinesTable({
  lines,
  selectedId,
  onSelect,
  onRuleKeyClick,
  showTotal = false,
  showLoanNumber = false
}: CommissionLinesTableProps) {
  const storedTotal = sumCents(lines.map(line => line.payAmount));
  const adjustedTotal = sumCents(lines.map(line => line.adjustedAmount));
  const leadingColumns = showLoanNumber ? 5 : 4;
  return <div className="overflow-x-auto" data-id="element-502" data-mp="2nqpynt4gex3a">
      <table className="w-full border-collapse" data-id="element-503" data-mp="2npomtmfm66ki">
        <thead data-id="element-504" data-mp="31hl2gr03io00">
          <tr data-id="element-505" data-mp="31ie9zu6vphj7">
            {showLoanNumber && <Th data-id="element-506" data-mp="2wjta5vvjgzva">Loan</Th>}
            <Th data-id="element-507" data-mp="2wjjjs76l0qp2">Payee</Th>
            <Th data-id="element-508" data-mp="2wl4i87w0ots7">Component</Th>
            <Th data-id="element-509" data-mp="2wlw955eo63c5">Rule Key</Th>
            <Th align="right" data-id="element-510" data-mp="28tz3jw1tc7jh">BPS</Th>
            <Th align="right" data-id="element-511" data-mp="28v0f01mqqw1q">
              Adjusted
              <span className="block text-[11px] font-normal text-sf-weak" data-id="element-512" data-mp="24sx0130v59bp">adj. loan × BPS · pays</span>
            </Th>
            <Th align="right" className="font-normal" data-id="element-513" data-mp="2isoiph2brdw1">
              Stored (unadjusted)
              <span className="block text-[11px] text-sf-weak" data-id="element-514" data-mp="2dssebgpptkfy">reference</span>
            </Th>
            <Th data-id="element-515" data-mp="3u7232bxvdb7b">Period</Th>
            <Th data-id="element-516" data-mp="3u6a7300d1bgi">Status</Th>
          </tr>
        </thead>
        <tbody data-id="element-517" data-mp="3p8h22aasmnht">
          {lines.map(line => {
          const isSelected = line.id === selectedId;
          const signed = line.status === 'Adjustment' || line.status === 'Manual';
          return <tr key={line.id} onClick={() => onSelect(line.id)} className={`cursor-pointer transition-colors duration-150 ease-out ${isSelected ? 'bg-sf-blue-light' : 'hover:bg-[#FAFAF9]'}`} data-id="element-518" data-mp="54ukfyiwban8">
                {showLoanNumber && <Td className={`whitespace-nowrap text-xs ${isSelected ? 'shadow-[inset_3px_0_0_#0176D3]' : ''}`} data-id="element-519" data-mp="3dl79j3gzly5n">{line.loanNumber}</Td>}
                <Td className={!showLoanNumber && isSelected ? 'shadow-[inset_3px_0_0_#0176D3]' : ''} data-id="element-520" data-mp="3ijig95bzjsjn">
                  <button type="button" aria-pressed={isSelected} onClick={event => {
                event.stopPropagation();
                onSelect(line.id);
              }} className="whitespace-nowrap rounded text-left font-semibold text-sf-blue hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sf-blue" data-id="element-521" data-mp="3nibwh5inpxni">
                    {line.payee}
                  </button>
                  <span className="block whitespace-nowrap text-xs text-sf-weak" data-id="element-522" data-mp="2nbyekxbun2hx">{line.payeeRole}</span>
                </Td>
                <Td data-id="element-523" data-mp="2idwy8k5t5ha5">
                  <span className="flex flex-wrap items-center gap-1.5" data-id="element-524" data-mp="2dd943pma8kbp">
                    <span className="whitespace-nowrap" data-id="element-525" data-mp="2dfd7m6g9rhbg">{line.component}</span>
                    {line.splitPct !== undefined && <>
                        <Badge tone="info" data-id="element-526" data-mp="24jx9vece8i0l">Split {line.splitPct}%</Badge>
                        <NewBadge label="NEW" data-id="element-527" data-mp="1zkszikdqge8o" />
                      </>}
                    {line.status === 'Manual' && <NewBadge label="NEW" data-id="element-528" data-mp="1unu5s2xze9b0" />}
                  </span>
                  {line.bpsSource && <span className="block whitespace-nowrap text-[11px] text-sf-weak" data-id="element-529" data-mp="1pmd6gbv5auwj">{line.bpsSource} · Bonus: none</span>}
                </Td>
                <Td data-id="element-530" data-mp="1lnxmkgud22nc">
                  {isPlanRuleKey(line.ruleKey) ? <button type="button" onClick={event => {
                event.stopPropagation();
                onRuleKeyClick();
              }} className="whitespace-nowrap rounded font-mono text-[11px] text-sf-blue hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sf-blue" data-id="element-531" data-mp="2ad0ocf4pvihm">
                      {line.ruleKey}
                    </button> : <span className="whitespace-nowrap font-mono text-[11px] text-sf-weak" data-id="element-532" data-mp="1wizm3bek3dci">{line.ruleKey}</span>}
                </Td>
                <Td align="right" data-id="element-533" data-mp="1mm4qb09o3r1x">
                  {line.bps === null ? <span className="text-sf-weak" data-id="element-534" data-mp="3czlque8wr4ea">{line.status === 'Skipped' ? '—' : 'Fixed'}</span> : formatBps(line.bps, line.component === 'Monthly Bonus' && !isOverrideRow(line))}
                </Td>
                <Td align="right" className="font-semibold" data-id="element-535" data-mp="15bhkx98f5aoh">
                  <Money value={line.adjustedAmount} signed={signed} data-id="element-536" data-mp="11d0aafpshi2b" />
                </Td>
                <Td align="right" data-id="element-537" data-mp="1f3n8m6rn15xv">
                  <StoredReference value={line.payAmount} signed={signed} compact data-id="element-538" data-mp="1f4onynfosele" />
                </Td>
                <Td className="whitespace-nowrap" data-id="element-539" data-mp="1abc7j0l5ve3q">
                  {line.postedTo ? <span data-id="element-540" data-mp="1ozqbjeubd8lv">
                      {line.period} <span className="text-sf-weak" data-id="element-541" data-mp="1k2qflwr6hm8h">→</span> {line.postedTo}
                    </span> : line.period}
                </Td>
                <Td data-id="element-542" data-mp="1t3ejvzqrs886">
                  <StatusBadge status={line.status} data-id="element-543" data-mp="1t2wrje78ae5j" />
                </Td>
              </tr>;
        })}
          {showTotal && <tr className="bg-[#FAFAF9]" data-id="element-544" data-mp="3qb37rg8cf9l">
              <Td colSpan={leadingColumns} className="font-semibold" data-id="element-545" data-mp="3uv1c24mee968">
                Total for this loan <span className="font-normal text-sf-weak" data-id="element-546" data-mp="1yrmzpft3obxd">· {lines.length} lines, all payees</span>
              </Td>
              <Td align="right" className="text-[14px] font-bold" data-id="element-547" data-mp="23pohlxesi579">
                <Money value={adjustedTotal} data-id="element-548" data-mp="28pkkftbr3ql8" />
              </Td>
              <Td align="right" data-id="element-549" data-mp="28o9fhq6kkcsj">
                <StoredReference value={storedTotal} compact data-id="element-550" data-mp="2cr64pzqm775g" />
              </Td>
              <Td colSpan={2} className="text-xs text-sf-weak" data-id="element-551" data-mp="2cp1t0vmh53um">
                Adjusted pays
              </Td>
            </tr>}
        </tbody>
      </table>
    </div>;
}